import React, { useState } from 'react';
import posthog from 'posthog-js';
import { motion, AnimatePresence } from 'framer-motion';
import { ADOBE_EXCHANGE_URL } from '../constants/urls';

interface PricingProps {
  onPurchase: () => void;
}

const Pricing: React.FC<PricingProps> = ({ onPurchase }) => {
  const [activeTab, setActiveTab] = useState<'free' | 'lifetime'>('free');

  const freeFeatures = [
    '7-day full access to ColorCache',
    'No credit card required',
    'One-click HEX and RGB color copying',
    'Drag & drop brand logos into Premiere Pro',
    'Integrated typography and font notes',
    'Sync palettes across all your projects',
    'Export your palettes and share them with your team',
  ];

  const paidFeatures = [
    'One-click HEX and RGB color copying',
    'Drag & drop brand logos into Premiere Pro',
    'Integrated typography and font notes',
    'Sync palettes across all your projects',
    'Export your palettes and share them with your team',
  ];

  return (
    <section
      id="pricing"
      className="pricing-section">
      <div className="container">
        <div className="pricing-content text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            viewport={{ once: true, margin: '-100px' }}>
            Get ColorCache
          </motion.h2>
          <motion.p
            className="mb-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            viewport={{ once: true, margin: "-100px" }}
          >
            No more digging for brand guides & logos. Start saving time today!
          </motion.p>

          <div className="pricing-toggle">
            <div style={{ display: 'flex', position: 'relative' }}>
              <button
                className={`toggle-btn ${activeTab === 'free' ? 'active' : ''}`}
                onClick={() => { setActiveTab('free'); posthog.capture('pricing_tab_changed', { tab: 'free' }); }}
                style={{ position: 'relative', zIndex: 1 }}
              >
                Free
              </button>
              <button
                className={`toggle-btn ${activeTab === 'lifetime' ? 'active' : ''}`}
                onClick={() => { setActiveTab('lifetime'); posthog.capture('pricing_tab_changed', { tab: 'pro' }); }}
                style={{ position: 'relative', zIndex: 1 }}
              >
                Pro
              </button>
              <motion.div
                className="toggle-slider"
                initial={false}
                animate={{
                  left: activeTab === 'free' ? '4px' : 'calc(50% + 0px)',
                  width: 'calc(50% - 4px)'
                }}
                transition={{ type: 'spring', stiffness: 400, damping: 35 }}
              />
            </div>
          </div>

          <motion.div
            className="pricing-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <AnimatePresence mode="wait">
              {activeTab === 'free' ? (
                <motion.div
                  key="free"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ display: 'flex', flexDirection: 'column', height: '100%', flexGrow: 1 }}
                >
                  <div className="pricing-header">
                    <h3>Free</h3>
                    <p className="pricing-subtitle">7-day free trial</p>
                  </div>

                  <div className="pricing-features">
                    {freeFeatures.map((feature, index) => (
                      <div key={index} className="pricing-feature">
                        <span className="check">✓</span>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div style={{ marginTop: 'auto' }}>
                    <motion.a
                      href={ADOBE_EXCHANGE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary btn-large"
                      style={{ textDecoration: 'none', display: 'block' }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      onClick={() => posthog.capture('install_clicked', { source: 'pricing' })}
                    >
                      Install ColorCache
                    </motion.a>
                  </div>
                  <p className="pricing-disclaimer">
                    By using ColorCache, you agree to our{' '}
                    <a
                      href="https://codec422.com/eula"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="disclaimer-link">
                      Terms and Conditions
                    </a>{', '}
                    <a
                      href="https://codec422.com/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="disclaimer-link">
                      License Agreement
                    </a>{' '}and{' '}
                    <a
                      href="https://codec422.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="disclaimer-link">
                      Privacy Policy
                    </a>
                  </p>
                </motion.div>
              ) : (
                <motion.div
                  key="annual"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ display: 'flex', flexDirection: 'column', height: '100%', flexGrow: 1 }}
                >
                  <div className="pricing-header">
                    <h3>Full Access</h3>
                    <div className="price">
                      <span className="currency">$</span>
                      <span className="amount">9</span>
                      <span className="currency">.99</span>
                    </div>

                    <p className="pricing-subtitle">billed annually</p>
                  </div>

                  <div className="pricing-features">
                    {paidFeatures.map((feature, index) => (
                      <div key={index} className="pricing-feature">
                        <span className="check">✓</span>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div style={{ marginTop: 'auto' }}>
                    <motion.button
                      className="btn btn-primary btn-large"
                      onClick={onPurchase}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      Buy ColorCache License
                    </motion.button>
                  </div>
                  <div style={{ marginTop: '16px' }}>
                    <a
                      href="mailto:team@codec422.com"
                      style={{
                        color: '#ffffff',
                        textDecoration: 'none',
                        fontSize: '0.9rem',
                        transition: 'opacity 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'}
                      onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                      onClick={() =>
                        posthog.capture('team_licensing_inquiry_clicked', { source: 'pricing_pro' })
                      }
                    >
                      Need team licensing? Contact us for discounts
                    </a>
                  </div>
                  <p className="pricing-disclaimer">
                    Annual subscription for full access to all features. By using ColorCache, you agree to our{' '}
                    <a
                      href="https://codec422.com/eula"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="disclaimer-link">
                      Terms and Conditions
                    </a>{', '}
                    <a
                      href="https://codec422.com/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="disclaimer-link">
                      License Agreement
                    </a>{' '}and{' '}
                    <a
                      href="https://codec422.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="disclaimer-link">
                      Privacy Policy
                    </a>
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
